import { useEffect, useState } from 'react';
import { MessageSquare, Mail, HardDrive, CreditCard, Bell, Save, AlertTriangle, KeyRound, Send, MapPin, Video, Ruler, Percent, Truck } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { CardSkeleton } from '../../components/skeleton';
import { Toggle } from '../../components/toggle';
import { extractErr } from '../../api/error-message';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, matchesQuery } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';

// Wired to GET/PUT /admin/config. Field metadata (group/type/options) comes from
// the server catalogue; secrets arrive masked and are only sent back when the
// admin types a new value.

type FieldType = 'text' | 'secret' | 'toggle' | 'select' | 'number';

interface ConfigField {
  key: string;
  label: string;
  type: FieldType;
  group: string;
  description: string | null;
  options: string[] | null;
  placeholder: string | null;
  value: string | number | boolean | null;
  isSet: boolean;
}

interface ConfigGroup {
  id: string;
  title: string;
  description: string;
}

interface ConfigResponse {
  groups: ConfigGroup[];
  fields: ConfigField[];
}

const GROUP_ICON: Record<string, React.ReactNode> = {
  sms: <MessageSquare size={18} />,
  email: <Mail size={18} />,
  storage: <HardDrive size={18} />,
  payments: <CreditCard size={18} />,
  maps: <MapPin size={18} />,
  channels: <Bell size={18} />,
  media: <Video size={18} />,
  catalog: <Ruler size={18} />,
  fees: <Percent size={18} />,
  auth: <KeyRound size={18} />,
  delivery: <Truck size={18} />,
};
// Icon chip tint per group — soft palette hues only (v2 kumkum/jamun/haldi/
// mehendi/neel/sindoor); unknown groups fall back to the admin accent-soft.
const GROUP_TONE: Record<string, string> = {
  sms: 'bg-kumkum-100 text-kumkum-700',
  email: 'bg-sindoor-100 text-sindoor-700',
  storage: 'bg-jamun-100 text-jamun-700',
  payments: 'bg-mehendi-100 text-mehendi-700',
  maps: 'bg-mehendi-100 text-mehendi-700',
  channels: 'bg-neel-100 text-neel-700',
  media: 'bg-jamun-100 text-jamun-700',
  catalog: 'bg-neel-100 text-neel-700',
  fees: 'bg-haldi-100 text-haldi-700',
  auth: 'bg-neel-100 text-neel-700',
  delivery: 'bg-mehendi-100 text-mehendi-700',
};

export function IntegrationsPage() {
  const [groups, setGroups] = useState<ConfigGroup[]>([]);
  const [fields, setFields] = useState<ConfigField[]>([]);
  const [edits, setEdits] = useState<Record<string, string | number | boolean>>({});
  const [loading, setLoading] = useState(true);
  const [savingGroup, setSavingGroup] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingMaps, setTestingMaps] = useState(false);
  const [query, setQuery] = useState('');
  const { confirm } = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<ConfigResponse>('/admin/config');
      setGroups(data.groups);
      setFields(data.fields);
      setEdits({});
    } catch (err) {
      setError(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const sendTestEmail = async () => {
    const to = testTo.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) {
      toast.error('Enter a valid recipient email to test');
      return;
    }
    setTestingEmail(true);
    try {
      const { data } = await api.post<{ sent: boolean; reason: string | null; detail: string | null }>(
        '/admin/config/test-email',
        { to },
      );
      if (data.sent) {
        toast.success(`Test email sent to ${to}`, { description: 'Check the inbox (and Spam).' });
      } else {
        toast.error(`Not sent — ${data.reason ?? 'unknown'}`, {
          description: data.detail ?? 'Save the email gateway settings first, then retry.',
        });
      }
    } catch (err) {
      toast.error(extractErr(err), { description: 'Save the email gateway first if you changed anything.' });
    } finally {
      setTestingEmail(false);
    }
  };

  const testMapsKey = async () => {
    setTestingMaps(true);
    try {
      const { data } = await api.post<{ ok: boolean; reason: string | null; detail: string | null }>(
        '/admin/config/test-maps',
        {},
      );
      if (data.ok) {
        toast.success('Google Maps key is valid', { description: 'Places & Geocoding APIs responded successfully.' });
      } else {
        toast.error(`Key check failed — ${data.reason ?? 'unknown'}`, {
          description: data.detail ?? 'Save the API key above first, then retry.',
        });
      }
    } catch (err) {
      toast.error(extractErr(err), { description: 'Save the Google Maps key first if you changed anything.' });
    } finally {
      setTestingMaps(false);
    }
  };

  const valOf = (f: ConfigField): string | number | boolean =>
    f.key in edits ? edits[f.key] : (f.value ?? (f.type === 'toggle' ? false : ''));

  const set = (key: string, v: string | number | boolean) =>
    setEdits((p) => ({ ...p, [key]: v }));

  // Current (possibly unsaved) text value of a config key, for the live hints.
  const textOf = (key: string): string => {
    const f = fields.find((x) => x.key === key);
    return f ? String(valOf(f) ?? '').trim() : '';
  };

  // Current (possibly unsaved) numeric value of a config key, for the fee preview.
  const pctOf = (key: string): number => {
    const f = fields.find((x) => x.key === key);
    if (!f) return 0;
    const n = Number(valOf(f));
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const saveGroup = async (groupId: string) => {
    const groupFields = fields.filter((f) => f.group === groupId);
    const updates = groupFields
      .filter((f) => f.key in edits)
      // Skip blank secrets — server treats them as "keep current" anyway.
      .filter((f) => !(f.type === 'secret' && String(edits[f.key]).trim() === ''))
      .map((f) => ({ key: f.key, value: edits[f.key] }));

    if (updates.length === 0) {
      toast.info('No changes to save');
      return;
    }
    // Turning a switch off (a gateway, a channel, a sign-in method) stops that
    // service for everyone on save — ask first. Turning things on needs no ask.
    const switchedOff = groupFields.filter(
      (f) => f.type === 'toggle' && f.key in edits && Boolean(f.value) && !edits[f.key],
    );
    if (switchedOff.length > 0) {
      const names = switchedOff.map((f) => f.label);
      const ok = await confirm({
        title: switchedOff.length === 1 ? `Switch off ${names[0]}?` : `Switch off ${switchedOff.length} settings?`,
        description: `${switchedOff.length === 1 ? 'It stops' : `${names.join(', ')} stop`} working for everyone as soon as you save. You can switch ${switchedOff.length === 1 ? 'it' : 'them'} back on here at any time.`,
        confirmLabel: 'Save and switch off',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setSavingGroup(groupId);
    try {
      await api.put('/admin/config', { updates });
      toast.success(`${groups.find((g) => g.id === groupId)?.title} saved`, {
        description: 'Changes are live and recorded in the audit trail.',
      });
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setSavingGroup(null);
    }
  };

  // There is no table here, so the search narrows the settings themselves: a
  // field shows when the query matches its section, label, key, help text or
  // current value (secrets match only "Set"). Hidden fields keep any unsaved
  // edit, and Save still sends it.
  const searching = query.trim().length > 0;
  const visibleGroups = groups
    .map((group) => ({
      group,
      fields: fields.filter(
        (f) =>
          f.group === group.id &&
          matchesQuery(
            [
              group.title,
              group.description,
              f.label,
              f.key,
              f.description,
              f.type === 'toggle'
                ? valOf(f) ? 'Enabled' : 'Disabled'
                : f.type === 'secret'
                  ? f.isSet ? 'Set' : ''
                  : valOf(f),
            ],
            query,
          ),
      ),
    }))
    .filter((g) => g.fields.length > 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Integrations" description="SMS, email, storage and payment gateway credentials." />
        <CardSkeleton cards={4} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Integrations" description="SMS, email, storage and payment gateway credentials." />
        <div className="alert alert-danger items-center">
          <p className="flex-1 text-[14px]">{error}</p>
          <button type="button" onClick={() => void load()} className="btn btn-secondary btn-sm">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Integrations" description="SMS, email, storage and payment gateway credentials." />

      <div className="alert alert-warning">
        <AlertTriangle size={18} className="mt-0.5 shrink-0" />
        <div>
          <p className="text-[14px] font-semibold text-ink-900">Sensitive credentials</p>
          <p className="mt-0.5 text-[13.5px] text-ink-700">
            Secrets are stored encrypted-at-rest and shown masked. Leave a secret field blank to keep its current value. Every change is audit-logged.
          </p>
        </div>
      </div>

      <TableSearch
        value={query}
        onChange={setQuery}
        placeholder="Search settings by section, name, key or value"
        resultCount={searching ? visibleGroups.reduce((n, g) => n + g.fields.length, 0) : undefined}
      />

      {searching && visibleGroups.length === 0 && (
        <EmptyState
          icon="search_off"
          title={`No matches for “${query.trim()}”`}
          body="Try another word, or clear the search to see every setting."
          action={<button type="button" onClick={() => setQuery('')} className="btn btn-secondary">Clear search</button>}
        />
      )}

      {visibleGroups.map(({ group, fields: groupFields }) => {
        const tone = GROUP_TONE[group.id] ?? 'bg-admin-soft text-admin-ink';
        return (
          <div key={group.id} className="overflow-hidden panel">
            <div className="flex items-center gap-3 border-b border-surface-line p-5">
              <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-md ${tone}`}>
                {GROUP_ICON[group.id] ?? <KeyRound size={18} />}
              </div>
              <div>
                <h2 className="text-[16px] font-semibold text-ink-900">{group.title}</h2>
                <p className="text-[13.5px] text-ink-500">{group.description}</p>
              </div>
            </div>

            <div className="p-5">
              <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                {groupFields.map((field) => (
                  <div key={field.key}>
                    <label className="field-label flex items-center">
                      {field.label}
                      {field.type === 'secret' && field.isSet && (
                        <span className="badge badge-success ml-2">Set</span>
                      )}
                    </label>

                    {field.type === 'toggle' && (
                      <div className="flex items-center justify-between rounded-sm border border-surface-line bg-surface-sunk px-3 py-2.5">
                        <p className="text-[14px] font-medium text-ink-900">{valOf(field) ? 'Enabled' : 'Disabled'}</p>
                        <Toggle checked={Boolean(valOf(field))} onChange={(v) => set(field.key, v)} aria-label={field.label} />
                      </div>
                    )}

                    {(field.type === 'text' || field.type === 'number' || field.type === 'secret') && (
                      <input
                        type={field.type === 'secret' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                        autoComplete={field.type === 'secret' ? 'new-password' : 'off'}
                        placeholder={field.placeholder ?? ''}
                        value={field.key in edits ? String(edits[field.key]) : (field.type === 'secret' ? '' : String(field.value ?? ''))}
                        onChange={(e) => set(field.key, field.type === 'number' ? Number(e.target.value) : e.target.value)}
                        className="input"
                      />
                    )}

                    {field.type === 'select' && (
                      <select
                        value={String(valOf(field))}
                        onChange={(e) => set(field.key, e.target.value)}
                        className="input"
                      >
                        {field.options?.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    )}

                    {field.description && <p className="field-help mt-1">{field.description}</p>}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => void saveGroup(group.id)}
                disabled={savingGroup === group.id}
                className="btn btn-primary"
              >
                <Save size={14} /> {savingGroup === group.id ? 'Saving…' : `Save ${group.title}`}
              </button>

              {group.id === 'email' && (
                <div className="panel-sunk mt-5 p-4">
                  <p className="text-[14px] font-semibold text-ink-900">Send a test email</p>
                  <p className="mt-0.5 text-[13.5px] text-ink-500">
                    Save the settings above first, then send a real test to confirm delivery. The result shows the exact provider error if it fails.
                  </p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <input
                      type="email"
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                      placeholder="recipient@example.com"
                      className="input flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => void sendTestEmail()}
                      disabled={testingEmail}
                      className="btn btn-secondary shrink-0"
                    >
                      <Send size={14} /> {testingEmail ? 'Sending…' : 'Send test email'}
                    </button>
                  </div>
                </div>
              )}

              {group.id === 'fees' && (() => {
                const productPct = pctOf('fees.product_fee_pct');
                const deliveryPct = pctOf('fees.delivery_partner_fee_pct');
                const markup = productPct + deliveryPct;
                const sample = 1000;
                const shown = Math.round(sample * (1 + markup / 100) * 100) / 100;
                const inr = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                return (
                  <div className="panel-sunk mt-5 p-4">
                    <p className="text-[14px] font-semibold text-ink-900">What the customer will see</p>
                    <p className="mt-0.5 text-[13.5px] text-ink-500">
                      Both percentages are added to the seller's price and baked into the displayed price — there is no separate fee line in the app. Sellers are still paid against their own price.
                    </p>
                    <dl className="mt-3 space-y-1 text-[13.5px]">
                      <div className="flex justify-between"><dt className="text-ink-700">Seller's price</dt><dd className="font-tabular text-ink-900">{inr(sample)}</dd></div>
                      <div className="flex justify-between"><dt className="text-ink-700">Product fee ({productPct}%)</dt><dd className="font-tabular text-ink-900">+{inr(sample * productPct / 100)}</dd></div>
                      <div className="flex justify-between"><dt className="text-ink-700">Delivery partner fee ({deliveryPct}%)</dt><dd className="font-tabular text-ink-900">+{inr(sample * deliveryPct / 100)}</dd></div>
                      <div className="flex justify-between border-t border-surface-line pt-1 font-semibold">
                        <dt className="text-ink-900">Shown on the customer app</dt>
                        <dd className="font-tabular text-ink-900">{inr(shown)}</dd>
                      </div>
                    </dl>
                    <p className="mt-2 text-[12.5px] text-ink-500">
                      Applies to every product immediately on save — catalog, search, product page, cart and checkout. GST and the delivery charge at checkout are unchanged.
                    </p>
                  </div>
                );
              })()}

              {group.id === 'auth' && (() => {
                const webId = textOf('auth.google_server_client_id');
                const accepted = textOf('auth.google_client_ids');
                const enabledField = fields.find((f) => f.key === 'auth.google_enabled');
                const wantsGoogle = enabledField ? Boolean(valOf(enabledField)) : false;
                const live = wantsGoogle && webId.length > 0 && accepted.length > 0;
                // The Android client ID is the easy mistake — it fails at token
                // verification with a message that doesn't point back to here.
                const looksAndroid = webId.length > 0 && !webId.includes('.apps.googleusercontent.com');

                return (
                  <div className="mt-5 space-y-3">
                    <div
                      className={live ? 'alert alert-success items-center' : 'panel-sunk flex items-center gap-3 px-4 py-3'}
                    >
                      <span className={`h-2 w-2 shrink-0 rounded-full ${live ? 'bg-success' : 'bg-ink-400'}`} />
                      <p className="text-[13.5px] text-ink-700">
                        {live ? (
                          <>Google sign-in is <strong className="text-success-ink">live</strong> in the customer app.</>
                        ) : wantsGoogle ? (
                          <>
                            Turned on, but <strong>not showing yet</strong> — a client ID is still missing, and the
                            button would only dead-end. Fill both fields above, then save.
                          </>
                        ) : (
                          <>Google sign-in is off. Customers sign in with email or mobile number.</>
                        )}
                      </p>
                    </div>

                    {looksAndroid && (
                      <div className="alert alert-warning">
                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                        <p className="text-[13.5px] text-ink-700">
                          That doesn’t look like a client ID. It should end in{' '}
                          <code className="rounded-xs bg-surface-2 px-1 font-mono text-[12.5px]">.apps.googleusercontent.com</code>.
                        </p>
                      </div>
                    )}

                    <div className="panel-sunk p-4">
                      <p className="text-[14px] font-semibold text-ink-900">Where to find the client ID</p>
                      <ol className="mt-2 list-decimal space-y-1.5 pl-4 text-[13.5px] text-ink-700">
                        <li>
                          In Firebase, open the <strong>customer</strong> app
                          (<code className="rounded-xs bg-surface-2 px-1 font-mono text-[12.5px]">in.utsaha.customer</code>) and add your app’s
                          signing fingerprint. Google only creates sign-in credentials once a fingerprint exists.
                        </li>
                        <li>
                          Download <code className="rounded-xs bg-surface-2 px-1 font-mono text-[12.5px]">google-services.json</code> again and
                          replace the copy in the mobile app.
                        </li>
                        <li>
                          Inside it, take the client ID marked{' '}
                          <code className="rounded-xs bg-surface-2 px-1 font-mono text-[12.5px]">"client_type": 3</code> — that’s the web one.
                          Paste it into both fields above.
                        </li>
                      </ol>
                      <p className="mt-2 text-[12.5px] text-ink-500">
                        Saving takes effect within 30 seconds — customers see the change next time they open the app, with
                        no update required. A release build uses a different fingerprint, so add that one too before you ship.
                      </p>
                    </div>
                  </div>
                );
              })()}

              {group.id === 'delivery' && (() => {
                const maxKm = pctOf('delivery.radius_rule.max_radius_km') || 15;
                const jobKm = pctOf('delivery.partner_job_radius_km') || 10;
                const fee = pctOf('delivery.base_fee') || 0;
                const share = pctOf('delivery.partner_payout_pct') || 0;
                const enabledField = fields.find((f) => f.key === 'delivery.radius_rule.enabled');
                const limitOn = enabledField ? Boolean(valOf(enabledField)) : true;
                const partnerEarns = Math.round(fee * share) / 100;
                return (
                  <div className="panel-sunk mt-5 p-4">
                    <p className="text-[14px] font-semibold text-ink-900">What these settings decide</p>
                    <ul className="mt-2 space-y-1.5 text-[13.5px] text-ink-700">
                      <li>
                        <span className="font-semibold text-ink-900">Who delivers.</span>{' '}
                        {limitOn
                          ? `An order is offered to delivery partners when the customer is within ${maxKm} km of the seller's pickup point. Further than that and the seller is asked to arrange a courier instead.`
                          : 'The radius limit is off — every order is offered to delivery partners no matter how far away the customer is.'}
                      </li>
                      <li>
                        <span className="font-semibold text-ink-900">What partners see.</span>{' '}
                        A partner sees pickups within {jobKm} km of wherever they are right now, nearest first.
                      </li>
                      <li>
                        <span className="font-semibold text-ink-900">What they earn.</span>{' '}
                        ₹{partnerEarns.toLocaleString('en-IN')} per completed job, out of the ₹{fee.toLocaleString('en-IN')} job fee.
                      </li>
                      <li>
                        <span className="font-semibold text-ink-900">What the customer pays.</span>{' '}
                        {(() => {
                          const custFee = pctOf('delivery.customer_fee');
                          const freeKm = pctOf('delivery.customer_free_within_km');
                          const perKm = pctOf('delivery.customer_fee_per_km');
                          const freeOver = pctOf('delivery.customer_free_above_order_value');
                          const parts: string[] = [];
                          if (freeKm > 0) parts.push(`nothing within ${freeKm} km`);
                          parts.push(
                            perKm > 0
                              ? `₹${custFee.toLocaleString('en-IN')} plus ₹${perKm}/km beyond${freeKm > 0 ? ' that' : ''}`
                              : `₹${custFee.toLocaleString('en-IN')} otherwise`,
                          );
                          if (freeOver > 0) parts.push(`free on baskets of ₹${freeOver.toLocaleString('en-IN')} or more`);
                          return `${parts.join(', ')}.`;
                        })()}
                      </li>
                    </ul>
                    <p className="mt-2 text-[12.5px] text-ink-500">
                      A multi-seller order creates one job — and one job fee — per seller, but the customer is charged once, measured to the furthest seller. All distances are straight-line, not road distance.
                    </p>
                  </div>
                );
              })()}

              {group.id === 'maps' && (
                <div className="panel-sunk mt-5 p-4">
                  <p className="text-[14px] font-semibold text-ink-900">Validate the API key</p>
                  <p className="mt-0.5 text-[13.5px] text-ink-500">
                    Save the key above first, then run a live Geocoding check. This is the server-side key used by the app's address search — the map-tile SDK key lives in the mobile build, not here.
                  </p>
                  <button
                    type="button"
                    onClick={() => void testMapsKey()}
                    disabled={testingMaps}
                    className="btn btn-secondary mt-3"
                  >
                    <KeyRound size={14} /> {testingMaps ? 'Checking…' : 'Test API key'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
